// src/components/SmartNotificationDot.js

import React, { useState } from 'react';

const SmartNotificationDot = ({
  type = 'info',
  count = 0,
  priority = 'medium',
  context = 'general',
  animate = true,
  position = 'top-right',
  showTooltip = true,
  notifications = [],
  onClick = null,
  size = 'sm' // 'xs', 'sm', 'md', 'lg'
}) => {
  const [showTooltipState, setShowTooltipState] = useState(false);

  if (count === 0 && notifications.length === 0) return null;

  // Color configurations by type
  const typeStyles = {
    urgent: {
      bg: 'bg-red-500',
      ping: 'bg-red-400',
      text: 'text-white',
      border: 'border-red-600'
    },
    warning: {
      bg: 'bg-orange-500',
      ping: 'bg-orange-400',
      text: 'text-white',
      border: 'border-orange-600'
    },
    success: {
      bg: 'bg-green-500',
      ping: 'bg-green-400',
      text: 'text-white',
      border: 'border-green-600'
    },
    info: {
      bg: 'bg-blue-500',
      ping: 'bg-blue-400',
      text: 'text-white',
      border: 'border-blue-600'
    }
  };

  // Size configurations
  const sizeStyles = {
    xs: { dot: 'h-2 w-2', text: 'text-xs', ping: 'h-2 w-2' },
    sm: { dot: 'h-3 w-3', text: 'text-xs', ping: 'h-3 w-3' },
    md: { dot: 'h-4 w-4', text: 'text-sm', ping: 'h-4 w-4' },
    lg: { dot: 'h-5 w-5', text: 'text-base', ping: 'h-5 w-5' }
  };

  // Position configurations
  const positionStyles = {
    'top-right': 'absolute -top-1 -right-1',
    'top-left': 'absolute -top-1 -left-1',
    'bottom-right': 'absolute -bottom-1 -right-1',
    'bottom-left': 'absolute -bottom-1 -left-1',
    'center': 'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
  };

  // Animation configurations by priority
  const getAnimationClass = () => {
    if (!animate) return '';

    switch (priority) {
      case 'high':
        return type === 'urgent' ? 'animate-pulse' : 'animate-bounce';
      case 'medium':
        return 'animate-ping';
      case 'low':
        return 'animate-pulse';
      default:
        return 'animate-ping';
    }
  };

  const currentTypeStyle = typeStyles[type] || typeStyles.info;
  const currentSizeStyle = sizeStyles[size] || sizeStyles.sm;
  const displayCount = count || notifications.length;

  // Counter formatting
  const formatCount = (num) => {
    if (num > 99) return '99+';
    if (num > 9) return num.toString();
    return num.toString();
  };
  
  // Relative time formatting
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    // Handle both Firebase Timestamps and standard Date strings/objects
    const time = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'agora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return time.toLocaleDateString('pt-BR');
  };

  // Tooltip with notification details
  const renderTooltip = () => {
    if (!showTooltip || !showTooltipState || notifications.length === 0) return null;

    const sortedNotifications = notifications
      .sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return (priorityOrder[b.priority] || 1) - (priorityOrder[a.priority] || 1);
      })
      .slice(0, 5); // Show only the 5 most important

    return (
      <div className="absolute z-50 w-64 p-3 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm text-gray-800">
            Notificações ({notifications.length})
          </h4>
          {notifications.length > 5 && (
            <span className="text-xs text-gray-500">
              +{notifications.length - 5} mais
            </span>
          )}
        </div>
        
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {sortedNotifications.map((notification, index) => (
            <div
              key={notification.id || index}
              className="flex items-start space-x-2 p-2 rounded bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className={`w-2 h-2 rounded-full mt-1 ${typeStyles[notification.type]?.bg || 'bg-gray-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-800 truncate">
                  {notification.title || 'Notificação'}
                </p>
                <p className="text-xs text-gray-600 truncate">
                  {notification.message || 'Nova atualização disponível'}
                </p>
                <p className="text-xs text-gray-400">
                  {formatTime(notification.createdAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        {onClick && (
          <button
            onClick={onClick}
            className="w-full mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Ver todas as notificações
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShowTooltipState(true)}
      onMouseLeave={() => setShowTooltipState(false)}
    >
      <span className={`${positionStyles[position]} flex ${currentSizeStyle.dot}`}>
        {/* Ping animation */}
        {animate && (
          <span
            className={`
              ${getAnimationClass()} 
              absolute inline-flex 
              ${currentSizeStyle.ping} 
              rounded-full 
              ${currentTypeStyle.ping} 
              opacity-75
            `}
          />
        )}
        
        {/* Main dot */}
        <span
          className={`
            relative inline-flex rounded-full 
            ${currentSizeStyle.dot} 
            ${currentTypeStyle.bg}
            ${currentTypeStyle.border}
            border-2
            items-center justify-center
            cursor-pointer
            transition-all duration-200
            hover:scale-110
          `}
          onClick={onClick}
        >
          {/* Counter */}
          {displayCount > 0 && (
            <span className={`
              ${currentTypeStyle.text} 
              ${currentSizeStyle.text} 
              font-bold 
              leading-none
            `}>
              {formatCount(displayCount)}
            </span>
          )}
        </span>
      </span>

      {/* Tooltip */}
      {renderTooltip()}
    </div>
  );
};

// Component specific to item indicators
export const ItemNotificationIndicator = ({
  itemId,
  notifications = [],
  onClick = null
}) => {
  // Standardize filtering: always check metadata.itemId first, then fall back to entityId.
  const itemNotifications = notifications.filter(n => {
    const notificationItemId = n.metadata?.itemId || n.entityId;
    return notificationItemId === itemId;
  });

  if (itemNotifications.length === 0) return null;

  // Determine the type based on the highest priority notification
  const getHighestPriorityType = (notifications) => {
    const hasUrgent = notifications.some(n => n.type === 'urgent');
    const hasWarning = notifications.some(n => n.type === 'warning');
    const hasSuccess = notifications.some(n => n.type === 'success');
    
    if (hasUrgent) return 'urgent';
    if (hasWarning) return 'warning';
    if (hasSuccess) return 'success';
    return 'info';
  };

  // Determine the priority for animation purposes
  const getHighestPriority = (notifications) => {
    const hasHigh = notifications.some(n => n.priority === 'high');
    const hasMedium = notifications.some(n => n.priority === 'medium');
    
    if (hasHigh) return 'high';
    if (hasMedium) return 'medium';
    return 'low';
  };

  return (
    <SmartNotificationDot
      type={getHighestPriorityType(itemNotifications)}
      priority={getHighestPriority(itemNotifications)}
      notifications={itemNotifications}
      onClick={onClick}
      position="top-right"
      size="sm"
    />
  );
};

// Component specific to tab indicators
export const TabNotificationIndicator = ({
  tabCategory,
  tradeId, // tradeId is correctly received here
  notifications = [],
  onClick = null
}) => {
  // Filter notifications based on tab category
  const tabNotifications = notifications.filter(n => n.tabCategory === tabCategory);

  if (tabNotifications.length === 0) return null;

  // Specific logic for tab types
  const getTabType = (notifications, tabCategory) => {
    if (tabCategory === 'disputes') {
      return notifications.some(n => n.type === 'urgent') ? 'urgent' : 'warning';
    }
    
    const hasUrgent = notifications.some(n => n.type === 'urgent');
    const hasWarning = notifications.some(n => n.type === 'warning');
    
    if (hasUrgent) return 'urgent';
    if (hasWarning) return 'warning';
    return 'success';
  };

  return (
    <SmartNotificationDot
      type={getTabType(tabNotifications, tabCategory)}
      priority="medium"
      notifications={tabNotifications}
      onClick={onClick}
      position="top-right"
      size="sm"
    />
  );
};

export default SmartNotificationDot;

